// The requisition's Disposition & handoff tab (PLAN D18, S11; CR-034, CR-059, CR-060, CR-062, CR-016): the worklist with
// its designation, then per accepted application the handoff checklist and the HRIS confirmation, and the milestone
// dates. Once Filled the tab is read-only; the Archived banner (and Reopen) is the requisition's, drawn once above every
// tab by register.ts, never again inside this one. One page for every seat: the server's `can` decides each control (the
// Coordinator, and so the Recruiter, validates and completes; the Admin does both as its own act; HM and HRBP view), and
// field policy has already trimmed what a seat may not read. Composes D0 only, inside the shell's McRoot (McPage).

import { useQueryClient } from "@tanstack/react-query";
import {
  ErrorStrip, Flex, Pill, SectionTitle, Skeleton, Stack,
} from "@/mc";
import { callTool, clockNow, newIdempotencyKey, type AppError } from "@/api/runtime";
import { McPage } from "@/shell/page";
import { useRead } from "@/api/useTool";
import { useSession } from "@/session/useSession";
import { HandoffCard } from "./HandoffCard";
import { HrisCard } from "./HrisCard";
import { MilestoneDates } from "./MilestoneDates";
import { Worklist } from "./Worklist";
import { CLOSURE_LABELS } from "./labels";

import type { CloseoutOut, RunWrite, WorklistOut } from "./types";

export interface ClosureViewProps {
  rid: string;
  closeout: CloseoutOut | undefined;
  worklist: WorklistOut | undefined;
  designate?: string | undefined;
  runWrite: RunWrite;
  /** The business day (YYYY-MM-DD) a designation records as Date added. */
  today: string;
  error?: AppError | null | undefined;
  onRetry?: (() => void) | undefined;
}

function problemOf(error: AppError) {
  return { status: error.status, refusal_code: error.code, detail: error.message, request_id: error.requestId };
}

/** The worklist is read where the seat's manifest carries `disposition.open_worklist` (the closeout seats and the V1
 * readers); a seat the tab is offered to without it gets the tab without the worklist, never a refusal
 * strip (demo smoke D4). */
export function readsWorklist(can: (tool: string) => boolean): boolean {
  return can("disposition.open_worklist");
}

/** The tab as a pure view of its two reads: the SSR tests render it per seat. */
export function ClosureView({ rid, closeout, worklist, designate, runWrite, today, error, onRetry }: ClosureViewProps) {
  const readOnly = !!closeout?.archived;
  const accepted = (closeout?.applications ?? []).filter((app) => app.checklist.length || app.hris || app.ready_for_hris_at);
  return (
    <Stack>
      <Flex>
        <SectionTitle as="h2">{CLOSURE_LABELS.tab}</SectionTitle>
        {closeout ? <Pill>{closeout.status_word.word}</Pill> : null}
      </Flex>
      {error ? (
        <ErrorStrip message={error.message} onRetry={onRetry} problem={problemOf(error)} live  />
      ) : null}
      {!closeout && !worklist && !error ? <Skeleton /> : null}
      {worklist ? (
        <Worklist rid={rid} worklist={worklist} runWrite={runWrite} designate={designate} today={today} readOnly={readOnly} />
      ) : null}
      {accepted.map((app) => (
        <Stack key={app.application_id}>
          <HandoffCard app={app} runWrite={runWrite} readOnly={readOnly} />
          <HrisCard app={app} runWrite={runWrite} readOnly={readOnly} />
        </Stack>
      ))}
      {closeout ? <MilestoneDates dates={closeout.dates ?? []} filledAt={closeout.filled_at} clocks={closeout.clocks ?? []} /> : null}
    </Stack>
  );
}

export function ClosurePage({ rid, designate }: { rid: string; designate?: string }) {
  const client = useQueryClient();
  const { can } = useSession();
  const closeoutKey = ["closeout.open", rid] as const;
  const worklistKey = ["disposition.open_worklist", rid] as const;
  const closeout = useRead(closeoutKey, (): Promise<CloseoutOut> => callTool("closeout.open", { requisition_id: rid }));
  const worklist = useRead(worklistKey, (): Promise<WorklistOut> => callTool("disposition.open_worklist", { requisition_id: rid }),
    { enabled: readsWorklist(can) });

  const runWrite: RunWrite = async ({ tool, input, reason }) => {
    try {
      await callTool(tool, input, { idempotencyKey: newIdempotencyKey(), ...(reason ? { reason } : {}) });
    } finally {
      await Promise.all([
        client.invalidateQueries({ queryKey: closeoutKey }),
        client.invalidateQueries({ queryKey: worklistKey }),
      ]);
    }
  };

  const error = closeout.error ?? worklist.error ?? null;
  const retry = () => { void closeout.refetch(); void worklist.refetch(); };

  return (
    <McPage>
      <ClosureView rid={rid} closeout={closeout.data} worklist={worklist.data} designate={designate} runWrite={runWrite}
        today={new Date(clockNow()).toISOString().slice(0, 10)} error={error} onRetry={retry} />
    </McPage>
  );
}
